import type { RequestFlightAirportInterface } from "@/libs/server/goApiServer/flight/flightServiceInterfaces/flightAirportInterface";
import { ResponseFlightAirport } from "@/libs/server/goApiServer/flight/flightServiceInterfaces/flightAirportInterface";
import {
  type RequestFareUpsell,
  type RequestFlightSeoInterface,
  ResponseFlightSeoClass,
} from "@/libs/server/goApiServer/flight/flightServiceInterfaces/flightSeoInterface";
import type { RequestNewFlightSeoInterface } from "@/libs/server/goApiServer/flight/flightServiceInterfaces/newFlightSeoInterface";
import { ResponseNewFlightSeo } from "@/libs/server/goApiServer/flight/flightServiceInterfaces/newFlightSeoInterface";
import {
  type RequestOtherFlightsInterface,
  ResponseOtherFlights,
} from "@/libs/server/goApiServer/flight/flightServiceInterfaces/otherFlightsInterface";
import { SearchFareDetailErrorEnum } from "@/types/product/flights/flightEnumErrorCode";
import { jsonParse } from "@/utils/common/jsonHelper/jsonHelper";
import { apiRequest } from "@/utils/common/serviceApiHelper/serviceApiUtils";
import usePath from "@/utils/hooks/client/usePath";

import {
  type RequestAllFlightInterface,
  ResponseAllFlight,
} from "./flightServiceInterfaces/getAllFlightInterface";
import { GetDestinationsRecommended } from "./flightServiceInterfaces/getDestinationsRecommendedInterface";
import type { RequestFlightCheckBacklistStatus } from "./flightServiceInterfaces/getFlightCheckBacklistStatus";
import {
  ResponseFareUpSell,
  ResponseFlightPerks,
} from "./flightServiceInterfaces/getFlightFaresInterface";
import type { RequestFlightFindAirlinesInfoInterface } from "./flightServiceInterfaces/getFlightFindAirlinesInfo";
import type { RequestFlightFindAirportCodeInterface } from "./flightServiceInterfaces/getFlightFindAirportCodeInterface";
import type { RequestFlightHistoryInterface } from "./flightServiceInterfaces/getFlightHiistoryInterface";
import type { RequestFlightLandingInterface } from "./flightServiceInterfaces/getFlightLandingInterface";
import { ResponseFlightLanding } from "./flightServiceInterfaces/getFlightLandingInterface";
import {
  type RequestFlightOfferInterface,
  ResponseFlightOffer,
} from "./flightServiceInterfaces/getFlightOffer";
import type { RequestRouteListInterface } from "./flightServiceInterfaces/getRouteListInterface";
import { ResponseRouteList } from "./flightServiceInterfaces/getRouteListInterface";
import { FlightAllocateResponse } from "./flightServiceInterfaces/postFlightAllocateInterface";
import type { RequestFlightFaresDetailInterface } from "./flightServiceInterfaces/postFlightFaresDetailInterface";
import {
  ResponseFareSelectedPresenter,
  ResponseSearchFlightFareDetailInterface,
} from "./flightServiceInterfaces/postFlightSearchFaresDetailInterface";
import {
  type FlightAllocateParamsInterface,
  type FlightSeatsBaseResponse,
  type FlightSeatsInterface,
  ResponseSeatMap,
} from "./flightServiceInterfaces/PostFlightSeatsInterface";

class FlightServiceApi {
  private baseUrl: string;

  private basePath: string;

  private basePathDeal: string;

  private basePathDestination: string;

  private basePathV2: string;

  private basePathV3Flight: string;

  private basePathV5Flight: string;

  private airport: string;

  private airportV3: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.basePath = "flight";
    this.basePathDeal = "v2/deals";
    this.basePathDestination = "v2/destination";
    this.basePathV2 = "v2/flight";
    this.basePathV3Flight = "v3/flight";
    this.basePathV5Flight = "v5/flight";
    this.airport = "v2/airport";
    this.airportV3 = "/v3/airport";
  }

  // ******************************************************
  // ****************** Method: GET ***********************
  // ******************************************************

  async getOtherFlights(params: RequestOtherFlightsInterface) {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.airport}/cities?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;
    return new ResponseOtherFlights(response);
  }

  async getDestinationsRecommended(type: string = "flight") {
    try {
      const { dynamicImportJsonPath } = usePath();
      const responseJson: GetDestinationsRecommended =
        await dynamicImportJsonPath(`${this.basePathV2}/SearchRecommended`);

      if (responseJson && responseJson.status) {
        return new GetDestinationsRecommended(responseJson);
      }

      // Fallback to API request if responseJson is invalid
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/SearchRecommended?type=${type}`,
        "GET",
      );
      return new GetDestinationsRecommended(response);
    } catch (error) {
      // console.error("error", error);
      return new GetDestinationsRecommended({});
    }
  }

  async getDestinations() {
    return apiRequest(this.baseUrl, `${this.basePath}/destinations`, "GET");
  }

  async getFlightHistory(params: RequestFlightHistoryInterface) {
    const queryParams = new URLSearchParams(params as any);
    return apiRequest(
      this.baseUrl,
      `${this.basePath}/history?accountId=10414&limit=50&page=1`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    );
  }

  async getFlightAirport(params: RequestFlightAirportInterface) {
    try {
      const queryParams = new URLSearchParams(params as any);

      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/bestCheapPrice?${queryParams}`,
        "GET",
      )) as any;
      if (response?.status && response.status === true) {
        return new ResponseFlightAirport(response);
      }
      return new ResponseFlightAirport();
    } catch (error) {
      return new ResponseFlightAirport();
    }
  }

  async getNewFlightSeo(params: RequestNewFlightSeoInterface) {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV3Flight}/FlightsSeo?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseNewFlightSeo(response);
  }

  async getFlightFindAirportCode(
    params: RequestFlightFindAirportCodeInterface,
  ) {
    const queryParams = new URLSearchParams(params as any);
    return apiRequest(
      this.baseUrl,
      `${this.basePath}/findAirportCode?airportCode=BKK`,
      "GET",
    );
  }

  async getFlightFindAirlinesInfo(
    params: RequestFlightFindAirlinesInfoInterface,
  ) {
    const queryParams = new URLSearchParams(params as any);
    return apiRequest(
      this.baseUrl,
      `${this.basePath}/findAirlinesInfo?airline=22`,
      "GET",
    );
  }

  async getFlightCheckBacklistStatus(params: RequestFlightCheckBacklistStatus) {
    const queryParams = new URLSearchParams(params as any);
    return apiRequest(
      this.baseUrl,
      `${this.basePath}/checkBacklistStatus?departureAirport=DMK&arrivalAirport=CNX&cardType=P&cardNo=a1523458412365&firstname=Thanawat&lastname=Thumbal`,
      "GET",
    );
  }

  async PostFaresDetail(
    params: RequestFlightFaresDetailInterface,
  ): Promise<ResponseSearchFlightFareDetailInterface> {
    const response = (await apiRequest(
      this.baseUrl,
      `v5/${this.basePath}/SearchFaresDetail`,
      "POST",
      params,
      true,
      undefined,
      undefined,
      undefined,
      false,
      false,
    )) as any;

    if (!response.status && response.status === false) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      return jsonParse(response);
    }

    return new ResponseSearchFlightFareDetailInterface(response);
  }

  async FareSelected(
    params: RequestFlightFaresDetailInterface,
  ): Promise<ResponseFareSelectedPresenter> {
    const response = (await apiRequest(
      this.baseUrl,
      `v5/${this.basePath}/fareSelected`,
      "POST",
      params,
      true,
      undefined,
      undefined,
      undefined,
      false,
      false,
    )) as any;

    if (!response.status && response.status === false) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      return jsonParse(response);
    }

    return new ResponseFareSelectedPresenter(response);
  }

  async PostFlightAllocate(
    params: FlightAllocateParamsInterface,
  ): Promise<FlightAllocateResponse> {
    const allocateBaseUrl =
      process.env.NEXT_PUBLIC_ALLOCATE_BASE_URL || this.baseUrl;
    try {
      const response = (await apiRequest(
        allocateBaseUrl,
        `v5/${this.basePath}/allocate`,
        "POST",
        params,
        true,
        undefined,
        undefined,
        undefined,
        false,
        false,
      )) as any;
      return new FlightAllocateResponse(response);
    } catch (error) {
      return new FlightAllocateResponse(jsonParse(error as string));
    }
  }

  async PostFlightSeats(
    params: FlightSeatsInterface,
  ): Promise<FlightSeatsBaseResponse> {
    const response = (await apiRequest(
      this.baseUrl,
      `v2/${this.basePath}/seatMap`,
      "POST",
      params,
      true,
      undefined,
      undefined,
      undefined,
      false,
      false,
    )) as any;

    if (!response.status && response.status === false) {
      const res = jsonParse(response);
      res.code = SearchFareDetailErrorEnum.FARES_DETAIL_NOT_FOUND_1;
      return jsonParse(res);
    }

    return new ResponseSeatMap(response);
  }

  async getFlightOffer(
    params: RequestFlightOfferInterface,
  ): Promise<ResponseFlightOffer> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathDeal}/FlightOffer?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseFlightOffer(response);
  }

  async getFlightSeo(params: RequestFlightSeoInterface) {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/FlightsSeo?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseFlightSeoClass(response);
  }

  async getAllAirport(params: RequestAllFlightInterface) {
    try {
      const queryParams = new URLSearchParams(params as any);
      const response = (await apiRequest(
        this.baseUrl,
        `${this.airportV3}/AllAirport?${queryParams}`,
        "GET",
      )) as any;
      if (response?.status && response.status === true) {
        return new ResponseAllFlight(response);
      }
      return new ResponseAllFlight();
    } catch (error) {
      return new ResponseAllFlight();
    }
  }

  async getRouteList(params: RequestRouteListInterface) {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.airport}/route?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseRouteList(response);
  }

  async getFlightLanding(params: RequestFlightLandingInterface) {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV5Flight}/landing?${queryParams}`,
      "GET",
    )) as any;
    return new ResponseFlightLanding(response);
  }

  async getFlightfaresUpsell(params: RequestFareUpsell) {
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/faresUpsell`,
      "POST",
      params,
    )) as any;

    return new ResponseFareUpSell(response);
  }

  async getFlightPerks(params: any) {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/perks?${queryParams}`,
      "GET",
    )) as any;

    return new ResponseFlightPerks(response);
  }
}

export default FlightServiceApi;
